// src/utils/publishResults.ts
// ─────────────────────────────────────────────────────────────────────────────
//  Publikace live výsledků do veřejných kolekcí
//    races/{raceId}/live_results  →  races/{raceId}/results_entries
//                                    races/{raceId}/results_stages
//                                    races/{raceId}/results_metadata/info
//
//  Používá se (signatura publishLiveResults se jen rozšiřuje – volá ji živé
//  bodování; asStaff a ve výsledku missingStartList, keptFromEarlier
//  a withoutCategory přibyly s bezpečností a, hlášku skládá publishNoticeText;
//  rundy jsou od fáze 2 nepovinné):
//   - JudgeView.handleFinishRace()      – při ukončení závodu
//   - AdminLiveResults                  – tlačítko „Publikovat výsledky“
//                                         + automaticky po opravě výsledku
//                                           (dílčí publikace onlyStartNumbers)
//
//  Rundy ze serveru (fáze 2 živého bodování, docs/live-faze2-navrh.md bod
//  4.5): volající rundy nepředávají – publikace si je přečte ze serveru
//  (loadLiveRoundsFromServer). Rundy načtené při otevření obrazovky mohly
//  být zastaralé: změna osoby na čísle a „nenastoupil“ za běhu se do výsledků
//  nedostaly. Střelce rund publikace sama doplní údaji startovní listiny
//  stejně jako obrazovka rozhodčího (roundShooterWithStartList – jméno,
//  nováček, zákaz nováčka); dřív to udělala jen obrazovka rozhodčího a Živé
//  výsledky předávaly holé rundy. Offline čtení skončí chybou a nic se
//  nezapíše.
//
//  Vlastnosti:
//   - idempotentní: opakované spuštění = přesné zrcadlo live_results
//     (přebytečné results_entries/results_stages se smažou)
//   - zapisuje přes utils/firestoreBatch.ts (dávky po 400 operacích, odeslané
//     postupně) – buď se zapíše celá dávka, nebo nic; metadata a bump
//     races.resultsPublishedAt jdou poslední, takže po chybě se cache
//     u střelců nezneplatní a opakování dopadne stejně
//   - žádné `undefined` hodnoty (Firestore je odmítá a shodí celý zápis)
//   - body zaokrouhleny (roundPoints) → žádné 435.59999999999997
//   - matchPoints = součet nastavených stanovišť (liveTotal s id z `stations`
//     – klíč mimo ně se nezapočítá) – uložený totalScore je od fáze 1 živého
//     bodování jen orientační kopie; volající předává živé výsledky i
//     stanoviště načtené ze serveru (loadLiveResultsFromServer,
//     loadLiveStationsFromServer – id jako u čtenářů), ne z místní mezipaměti
//     ani ze seznamu načteného při otevření obrazovky; bez stanovišť publikace
//     skončí chybou a nic nezapíše (docs/live-faze1-navrh.md, body 3.4 a 3.5)
//   - do race dokumentu zapíše resultsPublishedAt (+ resultsPublishedCount při
//     plné publikaci) → UserResults tím invaliduje localStorage cache na všech
//     zařízeních
//
//  Krok 9 redesignu (backlog #8, #27):
//   - každý záznam nese vedle textu `category` i `categoryId` z číselníku
//     utils/categories.ts. Věkové hranice z defaults/categories přes
//     categories.loadCategoryDefaults (žádné vlastní konstanty).
//   - categoryId zařazuje podle věku a pohlaví každého s údaji, člena
//     i nečlena (živá tabule řadí stejně). Bez údajů null.
//   - nováček → categoryId "novacci" (text zůstává podle věku).
//   - klíč dokumentu = členské číslo, jinak startovní číslo doplněné na 3 místa
//     jako u importu ("007"). Klíče se přidělují vždy nad VŠEMI střelci závodu
//     (i při dílčí publikaci – stejný klíč jako při plné) a nejdřív střelcům
//     s členským číslem (host nezabere klíč člena).
//   - matchPercent = podíl na bodech vítěze závodu (jako PractiScore „Match %"
//     a import), 2 desetinná místa – seasonRanking.computeRacePercent. Dřív
//     podíl z maximálně dosažitelného skóre (čtení terčů odpadlo).
//
//  Rozstřely (po kroku 10): rozstřel zadaný v živém bodování
//  (live_results.tiebreaker) se přenese do záznamu jako `tiebreaker` – pořadí
//  při shodě bodů pak sedí i ve veřejných výsledcích (utils/shootOff.ts).
//  Dílčí publikace zapisuje vždy celou skupinu shodných bodů zapisovaného
//  střelce, aby rozstřel (a štítek R) nesouhlasil jen u části skupiny.
//  Záznam nese i `userId` střelce (je-li v rundě) – „můj výsledek" se tak
//  nepáruje jen podle klíče (host s klíčem = startovní číslo by se kryl
//  s členským číslem jiného střelce).
//
//  Bezpečnost (a) (3. 10. 2026): profily users a přihlášky registrations už
//  nejsou veřejně čitelné. Kategorii a nováčka bere publikace z veřejné
//  startovní listiny races/{raceId}/registrations (utils/startList.ts) –
//  stejně jako tabule a rozhodčí:
//   - řádek s údaji z profilu → kategorie řádku (id i popisek). Řádek
//     počítá správce stejnou funkcí (ageCategoryOf) – id vychází stejně jako
//     dřív z profilu (test parity),
//   - střelec s účtem bez údajů ve startovní listině (řádek chybí nebo ho jen
//     přepnul rozhodčí; jejich počet vrací missingStartList):
//       · správce (asStaff) listinu před publikací synchronizuje z profilů
//         a přihlášek (syncStartList) a pro řádek, který ani pak údaje nemá,
//         čte profil jako dřív (resolveEntryCategory; bez profilu nebo
//         nečitelný profil → bez kategorie: "" + null),
//       · hlavní rozhodčí bez role users nečte vůbec (pravidla mu cizí profil
//         nedají) – ponechá kategorii z dřívější publikace téhož střelce
//         (earlierEntryCategory; přepočítá se jen nováček podle rozhodnutí
//         na řádku listiny), takže publikace hlavním rozhodčím kategorii
//         nezhorší; bez dřívějšího záznamu bez kategorie ("" + null) –
//         stejně u záznamu dřívější verze bez věkové kategorie v categoryId
//         (text "člen-*" / "nečlen": kategorii by z něj dostal jen člen),
//     UI hlásí publishNoticeText (keptFromEarlier, withoutCategory),
//   - nováček (resolveNewbie): rozhodnutí na řádku listiny (přepíná rozhodčí),
//     jinak přihláška závodu (čte správce a hlavní rozhodčí svého závodu),
//     jinak příznak z dat rund; zákaz nováčka (newbieDisabled) z řádku listiny
//     i z rundy ho ruší,
//   - jméno (rawName) jako tabule a obrazovka rozhodčího: startovní listina →
//     runda → „St. č. N", nikdy e-mail ani uid (publicShooterName); jméno
//     z hlavičky live výsledku jen u dopsaného střelce, který v rundách není.
//
//  Členství se nezveřejňuje (rozhodnutí majitele 4. 10. 2026): text
//  `category` nových záznamů je neutrální popisek věkové kategorie, stejný
//  jako ve výsledcích ("Muži", "Ženy", "Junioři", "Senioři", "Veteráni";
//  "" = neznámá) – entryCategoryText; dřív "člen-*" jen se zaplaceným
//  členstvím, jinak "nečlen". Čtecí strana (i verze v produkci od kroku 9)
//  bere kategorii z categoryId (resolveEntryCategoryId), text jen u záznamu
//  bez platného id: "" je nezařazené jako dřív "nečlen" a popisek převede
//  normalizeCategoryId zpět na totéž id – categoryId, pořadí, roční
//  hodnocení ani filtr kategorie se nemění (testy parity
//  v publishResults.test.ts). Člen a nečlen se stejným věkem a pohlavím mají
//  totožný záznam ve všech cestách (listina, profil, dřívější záznam).
//  Záznamy zveřejněné dřív si text ponechají, než se závod přepublikuje
//  (isMembershipCategoryText – počítá je Příprava dat).
// ─────────────────────────────────────────────────────────────────────────────

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
  type DocumentData,
  type DocumentReference,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import type { LiveResult, LiveRound, LiveStation, RoundShooter } from "../types/liveScoring";
import { roundPoints } from "./formatPoints";
import { createBatchWriter } from "./firestoreBatch";
import { firebaseErrorMessage } from "./firebaseErrors";
import { hasLiveResult, liveTotal, loadLiveRoundsFromServer } from "./liveResults";
import {
  RESULT_CATEGORIES,
  RESULT_CATEGORY_BY_ID,
  isResultCategoryId,
  loadCategoryDefaults,
  type ResultCategoryId,
} from "./categories";
import {
  ageCategoryOf,
  publishAgeLimitsOf,
  type AgeCategoryId,
  type PublishAgeLimits,
} from "./ageCategory";
import { computeRacePercent, normalizeSequenceNumber } from "./seasonRanking";
import { reportWarning } from "./reportError";
import { tiebreakerOf } from "./shootOff";
import {
  hitCountsForEntry,
  liveHitCounts,
  liveTieRulesApply,
  parseHitCounts,
  sameHitCounts,
  type HitStationLike,
} from "./tieRules";
import {
  hasProfileData,
  loadStartList,
  publicShooterName,
  raceYearOf,
  resolveNewbie,
  safeShooterName,
  syncStartList,
  type RoundNewbieFlags,
  type StartListRow,
} from "./startList";

// Dřívější exporty (testy, LiveRaceSetup…) – kód je v ageCategory.ts.
export { ageCategoryOf, publishAgeLimitsOf };
export type { AgeCategoryId, PublishAgeLimits };

/** Hodnota results_entries.source u publikace z live scoringu. */
const LIVE_SOURCE = "live_scoring";

// ── Pravidla zveřejnění podle data ukončení (fáze 4a, F1 a nález L4) ────────

/**
 * Od kdy platí pravidla zveřejnění fáze 4a (F1): zveřejní se jen aktivní
 * střelci rund s výsledkem a plná publikace maže podle skutečně zapsaných
 * klíčů. Rozhodnutí majitele: už ukončené a zveřejněné závody se nemění –
 * závod ukončený PŘED tímto okamžikem se (i při opravě, rozstřelu nebo
 * přepublikování) zveřejňuje podle dřívějších pravidel (nenastoupivší
 * s 0 body i dokumenty mimo rundy zůstanou). Hodnota = den nasazení 4a
 * (00:00 UTC); nasazuje-li se později a mezi tímto dnem a nasazením se
 * ukončil závod starou verzí, posunout na den nasazení.
 * Bez změny pravidel: rozhoduje races.finishedAt (neměnné od fáze 3).
 */
export const LIVE_F1_FROM_MS = Date.UTC(2026, 9, 9);

/**
 * Platí pro závod pravidla fáze 4a (F1)? Neukončený závod (běží, příprava –
 * ukončí ho už nová verze) ano; ukončený s finishedAt od LIVE_F1_FROM_MS
 * ano; ukončený dřív nebo bez finishedAt (starší) ne. Neznámý závod (čtení
 * selhalo) ne – bezpečnější je dřívější zveřejnění než tichá změna.
 */
export function liveF1Applies(race: { status?: unknown; finishedAt?: unknown } | null | undefined): boolean {
  if (!race) return false;
  if (race.status !== "finished") return true;
  const v = race.finishedAt as { toMillis?: () => number; seconds?: unknown } | number | null | undefined;
  let ms: number | null = null;
  if (typeof v === "number") ms = v;
  else if (v && typeof v.toMillis === "function") ms = v.toMillis();
  else if (v && typeof v.seconds === "number") ms = v.seconds * 1000;
  return ms !== null && Number.isFinite(ms) && ms >= LIVE_F1_FROM_MS;
}

/**
 * Pole pravidel shody 4b záznamu (utils/tieRules.ts): zásahy podle hodnot
 * z rozpadu na rány všech nastavených stanovišť (`hitCounts`, jen úplný)
 * a příznak úplnosti (`hitCountsComplete`). Zveřejněné výsledky podle nich
 * řadí shodu stejně jako tabule a Živé výsledky. Jen závody s pravidly 4b
 * (liveTieRulesApply) – starší závody se zapisují beze změny.
 */
export function entryHitFields(
  result: LiveResult | undefined,
  stations: ReadonlyArray<HitStationLike>
): { hitCounts?: Array<{ value: number; count: number }>; hitCountsComplete: boolean } {
  const hits = liveHitCounts(result, stations);
  return hits ? { hitCounts: hitCountsForEntry(hits), hitCountsComplete: true } : { hitCountsComplete: false };
}

// ── Kategorie ───────────────────────────────────────────────────────────────

/**
 * Text results_entries.category nového záznamu: neutrální popisek věkové
 * kategorie – stejný, jaký ukazují výsledky (categoryLabel: "Muži", "Ženy",
 * "Junioři", "Senioři", "Veteráni"; normalizeCategoryId ho převede zpět na
 * totéž id) –, "" = kategorie neznámá. Členství neprozrazuje (majitel
 * 4. 10. 2026); dřívější texty "člen-*" / "nečlen" zůstávají jen v záznamech
 * zveřejněných dřív.
 */
export function entryCategoryText(ageCategory: AgeCategoryId | null): string {
  return ageCategory ? RESULT_CATEGORY_BY_ID[ageCategory].label : "";
}

/** Kategorie, kterou publikace zapisuje do results_entries. */
export interface EntryCategory {
  /**
   * Text kategorie (entryCategoryText): popisek věkové kategorie, u nováčka
   * také podle věku; "" = věk a pohlaví neznámé. Nikdy členství.
   */
  category: string;
  /** Výsledková kategorie; null = nezařazeno (bez údajů ve startovní listině / v profilu). */
  categoryId: ResultCategoryId | null;
}

/** Záznam podle věkové kategorie (null = neznámá); nováček → id "novacci", text podle věku. */
const entryCategoryOf = (ageCategory: AgeCategoryId | null, newbie: boolean): EntryCategory => ({
  category: entryCategoryText(ageCategory),
  categoryId: newbie ? "novacci" : ageCategory,
});

/**
 * Kategorie záznamu z řádku startovní listiny (bezpečnost a – hlavní zdroj):
 *  - řádek s údaji z profilu (hasProfileData – synchronizoval správce) →
 *    kategorie řádku, text = její popisek,
 *  - bez údajů (řádek chybí, holý řádek od rozhodčího, host bez účtu) →
 *    "", null – jako u chybějícího profilu,
 *  - nováček → id "novacci", text zůstává podle věku.
 */
export function startListEntryCategory(
  row: StartListRow | null | undefined,
  newbie: boolean
): EntryCategory {
  const ageCategory = row && hasProfileData(row) ? row.categoryId ?? null : null;
  return entryCategoryOf(ageCategory, newbie);
}

/** Výsledek čtení profilu users/{uid}. */
export type ProfileRead =
  | { status: "ok"; data: DocumentData }
  /** Střelec bez účtu, nebo dokument users/{uid} neexistuje. */
  | { status: "missing" }
  /** Čtení selhalo (síť, oprávnění) – o střelci nic nevíme. */
  | { status: "error" };

/**
 * Kategorie záznamu (text + id) z profilu users – jen záloha správce pro
 * střelce bez údajů ve startovní listině (pravidlo 1:1 s výpočtem řádku
 * startList.computeStartListRow):
 *  - nečitelný profil → "", null (dřív tiše "člen"; ani nováček –
 *    newbieDisabled nejde ověřit),
 *  - bez profilu → "", null; nováček podle přihlášky platí,
 *  - s profilem → kategorie podle věku / pohlaví (členství nerozhoduje),
 *    text = její popisek,
 *  - nováček (přihláška "ano" a profil bez newbieDisabled) → id "novacci",
 *    text zůstává podle věku.
 */
export function resolveEntryCategory(
  profile: ProfileRead,
  registeredNewbie: boolean,
  year: number,
  limits: PublishAgeLimits
): EntryCategory {
  if (profile.status === "error") return entryCategoryOf(null, false);
  const ageCategory = profile.status === "ok" ? ageCategoryOf(profile.data, year, limits) : null;
  const newbieDisabled = profile.status === "ok" && profile.data.newbieDisabled === true;
  return entryCategoryOf(ageCategory, registeredNewbie && !newbieDisabled);
}

/**
 * Text dřívějšího live záznamu → věková kategorie: jen neutrální popisek
 * (entryCategoryText). Dřívější texty "člen-*" ne – věk nesl jen text člena,
 * "nečlen" žádný, takže kategorie z nich by v novém záznamu prozradila
 * členství (člen by ji dostal, nečlen stejného věku a pohlaví ne). "nečlen",
 * "", "Nováčci" a cizí text taky nic neříkají.
 */
const AGE_BY_ENTRY_TEXT = new Map<string, AgeCategoryId>();
RESULT_CATEGORIES.forEach(({ id }) => {
  if (id !== "novacci") AGE_BY_ENTRY_TEXT.set(entryCategoryText(id), id);
});

/** Text kategorie dřívější publikace, který prozrazuje členství: "člen", "člen-…", "nečlen" (i "Nečlen"). */
const MEMBERSHIP_TEXT = /^(ne)?člen(-|$)/i;

/**
 * Text results_entries.category, který prozrazuje členství – "člen-*"
 * a "nečlen" z publikace do 4. 10. 2026. Nové záznamy ho nemají; ve starších
 * ho přepíše přepublikování správcem. Příprava dat (securityMigration)
 * počítá živé výsledky, které ho ještě nesou – před finálními pravidly
 * má být 0.
 */
export function isMembershipCategoryText(value: unknown): boolean {
  return typeof value === "string" && MEMBERSHIP_TEXT.test(value.trim());
}

/**
 * Kategorie z dřívějšího live záznamu téhož střelce – publikace hlavním
 * rozhodčím u střelce bez údajů ve startovní listině: profil číst nesmí
 * a kategorii, kterou zapsala dřívější publikace (správcem, z profilu), nesmí
 * zhoršit na záznam bez kategorie.
 *  - věková kategorie z uloženého categoryId; u záznamu bez něj nebo
 *    dřívějšího nováčka z neutrálního popisku (publikace od 4. 10. 2026),
 *  - záznam dřívější verze s textem "člen-*" / "nečlen" bez věkové
 *    kategorie v categoryId (publikace před 3. 10. 2026 ho nezapisovala,
 *    nováček má "novacci") → bez kategorie (null) u člena i nečlena –
 *    kategorii jen členovi by nový záznam prozradil, kdo je člen; doplní ji
 *    přepublikování správcem (z listiny a profilů),
 *  - text = popisek té kategorie (dřívější "člen-*" / "nečlen" se
 *    nepřebírá – členství se nezveřejňuje),
 *  - nováček podle `newbie` (určí volající – rozhodnutí na řádku listiny,
 *    jinak stav dřívějšího záznamu) → "novacci", text zůstává podle věku.
 */
export function earlierEntryCategory(stored: DocumentData, newbie: boolean): EntryCategory {
  const storedId: unknown = stored.categoryId;
  const ageCategory: AgeCategoryId | null =
    isResultCategoryId(storedId) && storedId !== "novacci"
      ? storedId
      : AGE_BY_ENTRY_TEXT.get(stored.category) ?? null;
  return entryCategoryOf(ageCategory, newbie);
}

// ── Nováčci ─────────────────────────────────────────────────────────────────

// resolveNewbie a RoundNewbieFlags jsou od fáze 4a ve startList.ts (sdílí je
// tabule a Živé výsledky – utils/liveRanking.ts); export zůstává.
export { resolveNewbie };
export type { RoundNewbieFlags };

/** Dokument přihlášky (registrations). */
export interface RegistrationDoc {
  id: string;
  data: DocumentData;
}

/** Střelec přihlášky: pole userId, jinak id dokumentu. */
const registrationUid = (reg: RegistrationDoc): string =>
  typeof reg.data.userId === "string" && reg.data.userId ? reg.data.userId : reg.id;

/**
 * Nováček (isNewbie "ano") podle přihlášek závodu (registrations, PaymentRace)
 * → mapa uid → boolean. Víc přihlášek jednoho střelce (staré duplicity) →
 * nováček jen, když to říkají všechny (LiveRaceSetup mění jen první z nich).
 * Rozhodnutí na řádku startovní listiny má přednost (resolveNewbie).
 */
export function registrationNewbies(
  registrations: ReadonlyArray<RegistrationDoc>
): Map<string, boolean> {
  const map = new Map<string, boolean>();
  registrations.forEach((reg) => {
    const uid = registrationUid(reg);
    if (!uid) return;
    const isNewbie = reg.data.isNewbie === "ano";
    const previous = map.get(uid);
    map.set(uid, previous === undefined ? isNewbie : previous && isNewbie);
  });
  return map;
}

const toRegistrationDocs = (
  docs: ReadonlyArray<QueryDocumentSnapshot<DocumentData>>
): RegistrationDoc[] => docs.map((d) => ({ id: d.id, data: d.data() }));

/**
 * Nováčci podle přihlášek závodu; při chybě čtení null (zaloguje se
 * a rozhoduje startovní listina, pak data rund). Hlavní rozhodčí smí číst
 * přihlášky jen svého závodu (dotaz where raceId), ostatní ne. Čte se zvlášť
 * s vlastním catch – jako Promise.allSettled (lib ES2017 ho nezná): zamítnutí
 * přihlášek nesmí zahodit startovní listinu (dřív Promise.all obou kolekcí)
 * ani shodit publikaci, jinak by JudgeView nemohl ukončit závod.
 */
async function loadRegistrationNewbies(raceId: string): Promise<Map<string, boolean> | null> {
  try {
    const snap = await getDocs(
      query(collection(db, "registrations"), where("raceId", "==", raceId))
    );
    return registrationNewbies(toRegistrationDocs(snap.docs));
  } catch (err) {
    reportWarning("publishLiveResults.registrations", err, { raceId });
    return null;
  }
}

/** Profil users/{uid}; chyba čtení se zaloguje a vrátí jako status "error". */
async function readProfile(userId: string): Promise<ProfileRead> {
  if (!userId) return { status: "missing" };
  try {
    const snap = await getDoc(doc(db, "users", userId));
    return snap.exists() ? { status: "ok", data: snap.data() } : { status: "missing" };
  } catch (err) {
    reportWarning("publishLiveResults.profile", err, { userId });
    return { status: "error" };
  }
}

// ── Střelec rundy s údaji startovní listiny ─────────────────────────────────

/** Střelec z dokumentu rundy (starší rundy mohou mít jméno e-mail, příznaky nováčka chybí). */
export interface RoundShooterLike {
  startNumber: number;
  userId?: string;
  name?: string;
  isNewbie?: boolean;
  newbieDisabled?: boolean;
}

/**
 * Střelec rundy s údaji veřejné startovní listiny (uid → řádek) – jak ho
 * ukazuje obrazovka rozhodčího (a zapisuje do hlavičky živého výsledku) a jak
 * ho zveřejní publikace; tabule i Živé výsledky skládají jméno stejně:
 *  - jméno: listina → runda → „St. č. N" (publicShooterName – nikdy e-mail
 *    ani uid). Jméno z hlavičky živého výsledku se u střelce rundy nepoužije
 *    (po změně osoby na čísle by patřilo předchozímu střelci),
 *  - nováček: rozhodnutí na řádku listiny ("ano" / "ne"), jinak příznak rundy,
 *  - zákaz nováčka: z řádku listiny nebo z rundy.
 * Opakované použití nic nezmění – publikace z holých rund ze serveru proto
 * dá totéž co dřív z rund, které takto obohatila obrazovka rozhodčího.
 */
export function roundShooterWithStartList<T extends RoundShooterLike>(
  shooter: T,
  startList: ReadonlyMap<string, StartListRow>
): T & { name: string; isNewbie: boolean; newbieDisabled: boolean } {
  const row = shooter.userId ? startList.get(shooter.userId) : undefined;
  return {
    ...shooter,
    name: publicShooterName(
      safeShooterName(row?.name, shooter.userId) || shooter.name,
      shooter.startNumber,
      shooter.userId
    ),
    isNewbie: row?.isNewbie ? row.isNewbie === "ano" : shooter.isNewbie === true,
    newbieDisabled: row?.newbieDisabled === true || shooter.newbieDisabled === true,
  };
}

// ── Klíč dokumentu ──────────────────────────────────────────────────────────

/** Střelec pro přidělení klíče results_entries / results_stages. */
export interface EntryKeyShooter {
  startNumber: number;
  /** Členské číslo; "" = bez čísla. */
  sequenceNumber: string;
}

/**
 * Členské číslo z dat rundy / live výsledku: text beze změny (klíče členů se
 * nemění), kladné celé číslo doplněné na 3 místa (dřív publikaci shodilo),
 * cokoli jiného "".
 */
export function shooterSequenceNumber(raw: unknown): string {
  if (typeof raw === "string") return raw;
  if (typeof raw === "number" && Number.isInteger(raw) && raw > 0) {
    return normalizeSequenceNumber(raw);
  }
  return "";
}

/**
 * Základ klíče: členské číslo, jinak startovní číslo doplněné na 3 místa
 * ("7" → "007") – stejný tvar jako klíč importu (XLSX / PractiScore)
 * i normalizeSequenceNumber ve výsledcích.
 */
export function entryBaseKey(shooter: EntryKeyShooter): string {
  return shooter.sequenceNumber || normalizeSequenceNumber(shooter.startNumber);
}

/**
 * Klíče všech střelců závodu (startovní číslo → klíč). Nejdřív střelci
 * s členským číslem (v pořadí vstupu), potom ostatní – startovní číslo hosta
 * tak nikdy nezabere klíč člena. Shoda klíče → `${klíč}_${startovní číslo}`
 * jako dosud. Volá se nad všemi střelci i při dílčí publikaci, aby střelec
 * dostal pokaždé stejný klíč (dřív se při dílčí publikaci počítal jen nad
 * opravovaným střelcem a mohl přepsat záznam jiného).
 */
export function assignEntryKeys(
  shooters: ReadonlyArray<EntryKeyShooter>
): Map<number, string> {
  const keys = new Map<number, string>();
  const used = new Set<string>();
  const take = (shooter: EntryKeyShooter) => {
    if (keys.has(shooter.startNumber)) return;
    const base = entryBaseKey(shooter);
    let key = base;
    if (used.has(key)) {
      key = `${base}_${shooter.startNumber}`;
      // pojistka: tentýž tvar už nese jiný střelec (prakticky jen podivné členské číslo)
      for (let n = 2; used.has(key); n++) key = `${base}_${shooter.startNumber}_${n}`;
    }
    used.add(key);
    keys.set(shooter.startNumber, key);
  };
  shooters.forEach((s) => {
    if (s.sequenceNumber) take(s);
  });
  shooters.forEach((s) => {
    if (!s.sequenceNumber) take(s);
  });
  return keys;
}

// ── Publikace ───────────────────────────────────────────────────────────────

export interface PublishParams {
  raceId: string;
  /**
   * Datum závodu (text "YYYY-MM-DD", Timestamp) – určuje rok pro kategorii
   * stejně jako startovní listina (raceYearOf). Bez něj / neplatné = aktuální
   * rok.
   */
  raceDate?: unknown;
  /**
   * Rundy – volající (JudgeView, Živé výsledky) je nepředávají: bez nich si
   * je publikace přečte ze serveru (loadLiveRoundsFromServer, návrh fáze 2,
   * bod 4.5) – rundy načtené při otevření obrazovky mohou být zastaralé
   * (změna osoby na čísle, „nenastoupil“ za běhu). Předané i načtené rundy
   * publikace doplní údaji startovní listiny (roundShooterWithStartList),
   * na obohacení volajícím tedy nezáleží. Když rundy ze serveru nejdou
   * přečíst (offline), publikace skončí chybou a nic nezapíše.
   */
  rounds?: LiveRound[];
  liveResults: { [startNum: number]: LiveResult };
  /**
   * Nastavená stanoviště, jak je skládají čtenáři (`{ id: d.id, ...d.data() }`
   * – uložené id má přednost): stages a jediná stanoviště, ze kterých se
   * sčítají body (matchPoints). Volající je MUSÍ načíst ze serveru spolu
   * s živými výsledky (loadLiveStationsFromServer, návrh fáze 1, bod 3.5) –
   * seznam načtený při otevření obrazovky může být zastaralý (stanoviště
   * přidané za běhu by se do bodů nezapočítalo). Prázdný seznam = chyba
   * (liveTotal by bez stanovišť sečetl všechny klíče výsledků), nic se
   * nezapíše.
   */
  stations: LiveStation[];
  /**
   * Zapsat jen tyto startovní čísla (např. po opravě jednoho střelce).
   * V tomto režimu se nemažou přebytečné dokumenty a nepíšou metadata.
   * Fáze 4a (F1): požadovaný střelec bez výsledku se nezapíše a jeho
   * dřívější záznam se smaže; resultsPublishedCount se zapíše i tady (počet
   * záznamů po zápisu). Navíc oproti dřívějšku: smažou se starší kopie
   * zapisovaných střelců pod jiným klíčem (dřívější tvar klíče), zapíše se
   * i střelec, jehož záznam by nový klíč přepsal, a ostatním zveřejněným
   * live záznamům se dorovná matchPercent, když oprava změnila vítěze.
   *
   * Výjimka: závod zveřejněný dřívější verzí aplikace (live záznam bez pole
   * categoryId) se publikuje celý, i když je zadané onlyStartNumbers. Jinak
   * by categoryId dostal jen opravený střelec (filtr kategorií by ostatní
   * tiše vynechal) a starý klíč hosta, který dnes patří členovi, by zůstal
   * vedle nového záznamu (host dvakrát ve výsledcích). Neplatí pro smíšený
   * závod (vedle live i záznamy importu): plná publikace je zrcadlo a import
   * by bez potvrzení smazala – tam zůstane dílčí.
   */
  onlyStartNumbers?: number[];
  /**
   * Publikuje správce (role admin / superadmin – smí číst cizí profily
   * a přihlášky). true: startovní listina se před publikací synchronizuje
   * z profilů a přihlášek všech střelců s účtem (syncStartList; chyba jen
   * varování – publikuje se se stávající listinou) a střelec, jehož řádek
   * ani pak údaje nemá, dostane kategorii z profilu jako dřív. Výchozí false
   * (hlavní rozhodčí bez role): users se nečtou vůbec, střelec bez údajů
   * ve startovní listině si ponechá kategorii z dřívější publikace
   * (earlierEntryCategory), bez ní – i u záznamu dřívější verze bez
   * categoryId – zůstane bez kategorie ("" + categoryId null).
   */
  asStaff?: boolean;
  /**
   * Závod (status, finishedAt) – rozhoduje o pravidlech zveřejnění
   * (liveF1Applies). Bez něj si ho publikace přečte (getDoc).
   */
  race?: { status?: unknown; finishedAt?: unknown } | null;
}

export interface PublishResult {
  /** Zapsané záznamy (střelci). */
  written: number;
  /** Smazané dokumenty (results_entries + results_stages). */
  deleted: number;
  /**
   * Zapsaní střelci s účtem, kteří nemají ve startovní listině údaje z profilu
   * (ani po synchronizaci správcem). Doplní je obnovení startovní listiny
   * správcem a nová publikace (publishNoticeText).
   */
  missingStartList: number;
  /** Z missingStartList: kategorie převzatá z dřívější publikace téhož střelce (jen hlavní rozhodčí). */
  keptFromEarlier: number;
  /**
   * Z missingStartList: zveřejnění bez kategorie (category "", categoryId
   * null) – hlavní rozhodčí bez dřívějšího záznamu, správce bez profilu nebo
   * s nečitelným profilem.
   */
  withoutCategory: number;
}

/**
 * Varování po publikaci pro UI (JudgeView, Živé výsledky); null = není co
 * hlásit.
 *  - Správce: listinu před publikací synchronizoval a chybějící doplnil
 *    z profilu – hlásí jen střelce bez profilu (účet neexistuje, profil nejde
 *    přečíst), kteří zůstali bez kategorie.
 *  - Hlavní rozhodčí: střelci bez údajů ve startovní listině – kolik převzalo
 *    kategorii z dřívější publikace a kolik zůstalo bez kategorie; doplní je
 *    správce (obnoví startovní listinu a výsledky přepublikuje – jeho
 *    publikace listinu sama obnoví).
 */
export function publishNoticeText(
  result: Pick<PublishResult, "missingStartList" | "keptFromEarlier" | "withoutCategory">,
  asStaff: boolean
): string | null {
  if (asStaff) {
    return result.withoutCategory > 0
      ? `Bez profilu (účet neexistuje nebo profil nejde přečíst): ${result.withoutCategory} – ve výsledcích bez kategorie.`
      : null;
  }
  if (result.missingStartList === 0) return null;
  const parts: string[] = [];
  if (result.keptFromEarlier > 0) {
    parts.push(`kategorie převzata z dřívější publikace: ${result.keptFromEarlier}`);
  }
  if (result.withoutCategory > 0) {
    parts.push(`bez kategorie: ${result.withoutCategory}`);
  }
  return (
    `Bez údajů ve startovní listině: ${result.missingStartList}` +
    (parts.length > 0 ? ` (${parts.join(", ")})` : "") +
    ". Chybějící údaje doplní správce obnovením startovní listiny a přepublikováním výsledků (Opravy/Export)."
  );
}

interface PublishShooter extends EntryKeyShooter {
  userId: string;
  name: string;
  /** Příznaky nováčka z dat rundy – jen záloha (resolveNewbie). */
  round: RoundNewbieFlags;
}

/**
 * Střelci pro klíče publikace (krok 1): aktivní střelci rund (poslední
 * výskyt čísla) ∪ dokumenty výsledků mimo ně (klíč se jim přiděluje kvůli
 * stabilitě klíčů, od fáze 4a se ale nezveřejní – F1). Sdílí publikace
 * a porovnání se zveřejněnými výsledky (diffPublishedEntries).
 */
function liveKeyShooters(
  rounds: ReadonlyArray<LiveRound>,
  liveResults: { [startNum: number]: LiveResult }
): { byStart: Map<number, PublishShooter>; fromRounds: Map<number, RoundShooter> } {
  const byStart = new Map<number, PublishShooter>();
  const fromRounds = new Map<number, RoundShooter>();
  rounds.forEach((r) => {
    (r.shooters || []).filter((s) => s.active).forEach((s) => {
      byStart.set(s.startNumber, {
        startNumber: s.startNumber,
        userId: s.userId || "",
        sequenceNumber: shooterSequenceNumber(s.sequenceNumber),
        name: s.name || "",
        round: { isNewbie: s.isNewbie === true, newbieDisabled: s.newbieDisabled === true },
      });
      fromRounds.set(s.startNumber, s);
    });
  });
  Object.values(liveResults).forEach((lr) => {
    if (!lr || typeof lr.startNumber !== "number") return;
    if (!byStart.has(lr.startNumber)) {
      byStart.set(lr.startNumber, {
        startNumber: lr.startNumber,
        userId: lr.userId || "",
        sequenceNumber: shooterSequenceNumber(lr.sequenceNumber),
        name: lr.name || "",
        round: {},
      });
    }
  });
  return { byStart, fromRounds };
}

/**
 * Jméno záznamu jako tabule: listina → runda → „St. č. N" (střelec rundy má
 * jméno z kroku 3b vždy), live výsledek jen u dopsaného mimo rundy (nikdy
 * e-mail ani uid – výsledky jsou veřejné natrvalo).
 */
function entryRawName(
  row: StartListRow | undefined,
  sh: { startNumber: number; userId: string; name: string },
  result: LiveResult | undefined
): string {
  return publicShooterName(
    safeShooterName(row ? row.name : "", sh.userId) ||
      safeShooterName(sh.name, sh.userId) ||
      (result ? result.name : ""),
    sh.startNumber,
    sh.userId
  );
}

/** Chyba publikace bez stanovišť (PublishParams.stations prázdné). */
export const PUBLISH_NO_STATIONS_MESSAGE = "Závod nemá žádná stanoviště – výsledky nelze publikovat.";

/** Začátek chyby publikace, když rundy nejdou přečíst ze serveru (za dvojtečkou důvod). */
export const PUBLISH_ROUNDS_LOAD_MESSAGE = "Rundy se nepodařilo načíst ze serveru";

/**
 * Rundy pro publikaci ze serveru; chyba (offline) se zaznamená a vrátí jako
 * česká hláška – volající ji ukáže („Chyba publikace: …“, ukončení závodu
 * se nedokončí) a nic se nezapíše.
 */
async function loadRoundsForPublish(raceId: string): Promise<LiveRound[]> {
  try {
    return await loadLiveRoundsFromServer(raceId);
  } catch (err) {
    reportWarning("publishLiveResults.rounds", err, { raceId });
    throw new Error(`${PUBLISH_ROUNDS_LOAD_MESSAGE}: ${firebaseErrorMessage(err)}`);
  }
}

export async function publishLiveResults(params: PublishParams): Promise<PublishResult> {
  const { raceId, raceDate, liveResults, stations, onlyStartNumbers } = params;
  const asStaff = params.asStaff === true;

  // Bez stanovišť by liveTotal sečetl všechny klíče výsledků (prázdný seznam
  // = bez filtru, i podvržené klíče) a stages by byly prázdné – nepublikuje
  // se nic, ještě před čtením a zápisem (volající čte stanoviště ze serveru).
  if (!Array.isArray(stations) || stations.length === 0) {
    throw new Error(PUBLISH_NO_STATIONS_MESSAGE);
  }

  // Rundy ze serveru, nepředal-li je volající (návrh fáze 2, bod 4.5) –
  // kdo na kterém čísle střílí, podle aktuálního stavu, ne podle obrazovky
  // otevřené ráno
  const rounds = params.rounds ?? (await loadRoundsForPublish(raceId));

  // Pravidla zveřejnění podle ukončení závodu (F1 jen pro závody ukončené
  // po nasazení 4a – starší se nemění, nález L4)
  let raceForRules = params.race;
  if (raceForRules === undefined) {
    try {
      const snap = await getDoc(doc(db, "races", raceId));
      raceForRules = snap.exists() ? (snap.data() as { status?: unknown; finishedAt?: unknown }) : null;
    } catch (err) {
      reportWarning("publishLiveResults.race", err, { raceId });
      raceForRules = null;
    }
  }
  const f1 = liveF1Applies(raceForRules);
  // Pravidla shody 4b (počet zásahů do záznamu) – ne u závodu ukončeného
  // před nasazením 4b; když závod nejde přečíst, pole se zapíšou (čtenáři je
  // berou jen u závodů 4b – jinak by zveřejnění bez nich tiše řadilo jinak než
  // tabule; nález 4 kontroly 4b)
  const tieRules = raceForRules === null || liveTieRulesApply(raceForRules);

  // Rok závodu stejně jako startovní listina (text i Timestamp)
  const year = raceYearOf(raceDate) ?? new Date().getFullYear();

  // 1) Všichni střelci závodu = aktivní střelci z rund ∪ střelci, kteří mají
  //    live_result, ale v rundách nejsou (dopsaní ad hoc). Klíč: startNumber.
  //    Jméno a nováčka střelců rund doplní startovní listina (krok 3b).
  //    Fáze 4a (F1, otázky 8 a 17): klíče se dál přidělují nad všemi (stejný
  //    klíč jako dřív), ZVEŘEJNÍ se ale jen střelci rund s výsledkem
  //    (publishableOf) – ne nenastoupivší bez výsledku a ne dokumenty mimo
  //    aktivní střelce (neaktivní, dopsané mimo rundy), dokud je správce
  //    nevyřeší v Živých výsledcích.
  const { byStart, fromRounds } = liveKeyShooters(rounds, liveResults);
  const allShooters = Array.from(byStart.values());
  const inRounds = (startNumber: number): boolean => fromRounds.has(startNumber);

  // Klíče i vítěz se počítají nad VŠEMI střelci – dílčí publikace musí dát
  // střelci stejný klíč i procenta jako plná.
  const keys = assignEntryKeys(allShooters);
  const keyOf = (startNumber: number): string =>
    keys.get(startNumber) ?? normalizeSequenceNumber(startNumber);
  // Body = součet stanovišť při čtení (liveTotal, fáze 1 živého bodování –
  // docs/live-faze1-navrh.md, bod 3.4), ne uložený totalScore: ten je jen
  // orientační kopie od klienta a zápisy po stanovištích ho neudržují.
  // Jen nastavená stanoviště (id jako stages níže – uložené id má přednost,
  // tak je skládají čtenáři): body pod jiným klíčem (podvržené st01, starý
  // klíč po přečíslování) se nezveřejní, stejně jako na tabuli.
  const stationIds = stations.map((st) => st.id);
  const totalOf = (startNumber: number): number => liveTotal(liveResults[startNumber], stationIds);
  /**
   * Zveřejní se: aktivní střelec rund s výsledkem (aspoň jedno nastavené
   * stanoviště s body). Závod ukončený před 4a: všichni jako dřív.
   */
  const publishableOf = (startNumber: number): boolean =>
    !f1 || (inRounds(startNumber) && hasLiveResult(liveResults[startNumber], stationIds));
  // Vítěz jen ze zveřejňovaných (F1) – výsledek mimo rundy % neovlivní
  const topScore = allShooters.reduce(
    (max, s) => (publishableOf(s.startNumber) ? Math.max(max, totalOf(s.startNumber)) : max),
    0
  );

  // 2) Správce nejdřív synchronizuje startovní listinu z profilů a přihlášek
  //    (čerstvé hranice kategorií načte předem – synchronizace je vezme ze
  //    sdílené cache, záloha z profilu níže počítá se stejnými). Chyba
  //    synchronizace publikaci nezastaví: publikuje se se stávající listinou.
  let limits: PublishAgeLimits | null = null;
  if (asStaff) {
    limits = publishAgeLimitsOf(await loadCategoryDefaults(true));
    try {
      await syncStartList(raceId, raceDate, allShooters.map((s) => s.userId));
    } catch (err) {
      reportWarning("publishLiveResults.syncStartList", err, { raceId });
    }
  }

  // 3) Startovní listina, nováčci z přihlášek a dosavadní záznamy – paralelně.
  //    Startovní listina je veřejná; když ji nejde přečíst, publikace selže
  //    stejně jako u dosavadních záznamů (jinak by všechny tiše zveřejnila
  //    bez kategorie). Přihlášky selhat smí (loadRegistrationNewbies).
  const entriesRef = collection(db, `races/${raceId}/results_entries`);
  const stagesRef = collection(db, `races/${raceId}/results_stages`);
  const [startList, newbies, oldEntries, oldStages] = await Promise.all([
    loadStartList(raceId),
    loadRegistrationNewbies(raceId),
    getDocs(entriesRef),
    getDocs(stagesRef),
  ]);

  // 3b) Střelci rund s údaji startovní listiny stejně jako na obrazovce
  //     rozhodčího a tabuli (roundShooterWithStartList): jméno listina →
  //     runda → „St. č. N" (hlavička live výsledku se u nich nepoužije),
  //     zákaz nováčka z řádku listiny i z rundy. Dřív to dělala jen obrazovka
  //     rozhodčího před předáním rund – publikace z rund ze serveru tak zapíše
  //     totéž co dřív ukončení závodu (test parity) a publikace ze Živých
  //     výsledků (dřív holé rundy) dopadne stejně.
  fromRounds.forEach((source, startNumber) => {
    const sh = byStart.get(startNumber);
    if (!sh) return;
    const shown = roundShooterWithStartList(source, startList);
    sh.name = shown.name;
    sh.round = { isNewbie: shown.isNewbie, newbieDisabled: shown.newbieDisabled };
  });

  const stored = new Map<string, DocumentData>();
  oldEntries.docs.forEach((d) => stored.set(d.id, d.data()));

  // Dílčí publikace jen nad závodem zveřejněným touto verzí – live záznam bez
  // categoryId (dřívější publikace) znamená plnou publikaci, pokud v závodě
  // nejsou i záznamy importu (viz onlyStartNumbers).
  let hasLegacyLiveEntry = false;
  let hasOtherEntry = false;
  oldEntries.docs.forEach((d) => {
    const data = d.data();
    if (data.source !== LIVE_SOURCE) hasOtherEntry = true;
    else if (!("categoryId" in data)) hasLegacyLiveEntry = true;
  });
  const partial = !!onlyStartNumbers && !(hasLegacyLiveEntry && !hasOtherEntry);
  const requested =
    partial && onlyStartNumbers && onlyStartNumbers.length > 0
      ? new Set(onlyStartNumbers)
      : null;
  const toWrite = new Set<number>();
  allShooters.forEach((s) => {
    if ((!requested || requested.has(s.startNumber)) && publishableOf(s.startNumber)) toWrite.add(s.startNumber);
  });
  // Dílčí publikace: požadovaný střelec bez výsledku (hlavní rozhodčí smazal
  // jeho poslední stanoviště) – jeho dřívější záznam se smaže
  const dropped = new Set<number>();
  if (requested) requested.forEach((sn) => {
    if (!toWrite.has(sn)) dropped.add(sn);
  });

  /** Live záznam, který publikace zapsala dřív pro dané startovní číslo. */
  const isLiveEntryOf = (
    data: DocumentData | undefined,
    startNumber: number
  ): data is DocumentData =>
    !!data && data.source === LIVE_SOURCE && data.startNumber === startNumber;

  // 4) Dílčí publikace: rozložení klíčů se od poslední publikace mohlo změnit
  //    (např. host se starým klíčem, který teď patří členovi). Zapíše se proto
  //    i střelec, jehož live záznam leží pod klíčem zapisovaného (jinak by ze
  //    zveřejněných výsledků zmizel), a vlastník klíče, pod kterým leží starší
  //    kopie zapisovaného (jeho zápis kopii přepíše – jinak by tam zapisovaný
  //    zůstal podruhé a vlastník by chyběl).
  if (partial) {
    // Celá skupina shodných bodů (> 0) zapisovaných – rozstřel se týká všech
    const tiedTotals = new Set<number>();
    toWrite.forEach((startNumber) => {
      const total = totalOf(startNumber);
      if (total > 0) tiedTotals.add(total);
    });
    allShooters.forEach((s) => {
      if (tiedTotals.has(totalOf(s.startNumber)) && publishableOf(s.startNumber)) toWrite.add(s.startNumber);
    });

    const ownerOfKey = new Map<string, number>();
    keys.forEach((key, startNumber) => ownerOfKey.set(key, startNumber));
    const storedKeysOf = new Map<number, string[]>();
    oldEntries.docs.forEach((d) => {
      const data = d.data();
      if (data.source !== LIVE_SOURCE || typeof data.startNumber !== "number") return;
      const list = storedKeysOf.get(data.startNumber);
      if (list) list.push(d.id);
      else storedKeysOf.set(data.startNumber, [d.id]);
    });

    // Fronta: přidaný střelec se zkontroluje taky (jeho klíč může držet další)
    const pendingCheck = Array.from(toWrite);
    const enqueue = (other: unknown): void => {
      if (typeof other === "number" && byStart.has(other) && !toWrite.has(other) && publishableOf(other)) {
        toWrite.add(other);
        pendingCheck.push(other);
      }
    };
    for (let i = 0; i < pendingCheck.length; i++) {
      const startNumber = pendingCheck[i];
      const holder = stored.get(keyOf(startNumber));
      if (holder && holder.source === LIVE_SOURCE && holder.startNumber !== startNumber) {
        enqueue(holder.startNumber);
      }
      (storedKeysOf.get(startNumber) || []).forEach((id) => {
        if (id !== keyOf(startNumber)) enqueue(ownerOfKey.get(id));
      });
    }
  }
  const writeList = allShooters.filter((s) => toWrite.has(s.startNumber));

  // 5) Kategorie zapisovaných střelců ze startovní listiny. Střelci s účtem
  //    bez údajů v ní: správce čte jejich profil (jedno čtení na uid,
  //    paralelně) jako dřív; hlavní rozhodčí bez role users nečte vůbec
  //    a ponechá kategorii z dřívější publikace téhož střelce (jinak bez
  //    kategorie: "" + null). UI je spočítá (missingStartList a spol.).
  const withoutStartList = writeList.filter(
    (s) => !!s.userId && !hasProfileData(startList.get(s.userId))
  );
  const profiles = new Map<string, ProfileRead>();
  if (asStaff) {
    await Promise.all(
      Array.from(new Set(withoutStartList.map((s) => s.userId))).map(async (uid) => {
        profiles.set(uid, await readProfile(uid));
      })
    );
  }

  /**
   * Dřívější live záznam téhož střelce: podle uid; záznam bez uid (dřívější
   * verze) podle startovního čísla, a má-li střelec členské číslo, musí
   * sedět i klíč (= členské číslo, při shodě s příponou _startovní číslo).
   * Přednost má záznam pod klíčem střelce.
   */
  const earlierEntryOf = (sh: PublishShooter): DocumentData | undefined => {
    const sameShooter = (id: string, data: DocumentData): boolean => {
      if (data.source !== LIVE_SOURCE) return false;
      if (typeof data.userId === "string" && data.userId) return data.userId === sh.userId;
      if (data.startNumber !== sh.startNumber) return false;
      return !sh.sequenceNumber || id === sh.sequenceNumber || id.startsWith(`${sh.sequenceNumber}_`);
    };
    const ownKey = keyOf(sh.startNumber);
    const own = stored.get(ownKey);
    if (own && sameShooter(ownKey, own)) return own;
    let found: DocumentData | undefined;
    stored.forEach((data, id) => {
      if (!found && sameShooter(id, data)) found = data;
    });
    return found;
  };

  let keptFromEarlier = 0;
  let withoutCategory = 0;
  const categories = new Map<number, EntryCategory>();
  writeList.forEach((sh) => {
    const row = sh.userId ? startList.get(sh.userId) : undefined;
    // Host bez účtu nováčkem není (jako dosud)
    const newbie =
      !!sh.userId &&
      resolveNewbie(row, newbies ? newbies.get(sh.userId) : undefined, sh.round);
    if (!sh.userId || hasProfileData(row)) {
      categories.set(sh.startNumber, startListEntryCategory(row, newbie));
      return;
    }
    // Střelec s účtem bez údajů ve startovní listině (withoutStartList)
    const profile = profiles.get(sh.userId);
    const earlier = asStaff ? undefined : earlierEntryOf(sh);
    let value: EntryCategory;
    if (profile && limits) {
      value = resolveEntryCategory(profile, newbie, year, limits);
    } else if (earlier) {
      // Nováček: rozhodnutí na řádku listiny (přepnul ho rozhodčí) platí; bez
      // něj zůstane jako v dřívějším záznamu – ten znal i zákaz nováčka
      // z profilu (přihláška ho nezná); záznam dřívější verze nováčka nenese
      const decided = !!row && row.isNewbie !== undefined;
      const keepNewbie = sh.round.newbieDisabled !== true && earlier.categoryId === "novacci";
      value = earlierEntryCategory(earlier, decided ? newbie : keepNewbie);
      if (value.categoryId !== null) keptFromEarlier += 1;
    } else {
      value = startListEntryCategory(row, newbie);
    }
    if (value.categoryId === null) withoutCategory += 1;
    categories.set(sh.startNumber, value);
  });

  // 6) Sestavení dokumentů
  const entryDocs = writeList.map((sh) => {
    const result = liveResults[sh.startNumber];
    const totalScore = totalOf(sh.startNumber);
    const key = keyOf(sh.startNumber);
    const { category, categoryId } = categories.get(sh.startNumber) as EntryCategory;
    const row = sh.userId ? startList.get(sh.userId) : undefined;
    // Rozstřel z živého bodování (bez rozstřelu pole chybí – set() ho po
    // zrušení rozstřelu z dokumentu odstraní)
    const tiebreaker = tiebreakerOf(result);
    return {
      key,
      entry: {
        sequenceNumber: key,
        startNumber: sh.startNumber,
        // Jméno jako tabule: listina → runda → „St. č. N" (střelec rundy má
        // jméno z kroku 3b vždy), live výsledek jen u dopsaného mimo rundy
        // (nikdy e-mail ani uid – výsledky jsou veřejné natrvalo)
        rawName: entryRawName(row, sh, result),
        category,
        categoryId,
        division: "",
        matchPoints: totalScore,
        matchPercent: computeRacePercent({ matchPoints: totalScore }, { topScore }),
        valid: true,
        uploadedAt: serverTimestamp(),
        source: LIVE_SOURCE,
        ...(tiebreaker > 0 ? { tiebreaker } : {}),
        ...(sh.userId ? { userId: sh.userId } : {}),
        ...(tieRules ? entryHitFields(result, stations) : {}),
      },
      stages: {
        stages: stations.map((st) => ({
          name: st.name || "",
          points: roundPoints(result?.stations?.[st.id]?.score || 0, 2),
        })),
        uploadedAt: serverTimestamp(),
      },
    };
  });

  // 7) Co smazat a dorovnat
  //    Fáze 4a (F1): mazání podle klíčů SKUTEČNĚ zapisovaných záznamů – klíč
  //    přidělený nenastoupivšímu (bez výsledku) už záznam mít nesmí.
  const writtenKeys = new Set<string>(entryDocs.map((d) => d.key));
  // Závod ukončený před 4a: mazání jako dřív podle klíčů VŠECH střelců
  const assignedKeys = new Set<string>();
  keys.forEach((key) => assignedKeys.add(key));
  const keepKeys = f1 ? writtenKeys : assignedKeys;
  const staleIds = new Set<string>();
  const staleRefs: DocumentReference<DocumentData>[] = [];
  const percentUpdates: { key: string; matchPercent: number }[] = [];
  if (!partial) {
    // Plná publikace = zrcadlo: pryč vše, co se teď nezapisuje (i starší tvar
    // klíče téhož střelce, např. nedoplněné "7" vedle "007", a záznam
    // nenastoupivšího ze starší publikace).
    oldEntries.docs.forEach((d) => {
      if (!keepKeys.has(d.id)) staleIds.add(d.id);
    });
    oldStages.docs.forEach((d) => {
      if (!keepKeys.has(d.id)) staleIds.add(d.id);
    });
  } else {
    // Dílčí publikace: starší kopie zapisovaných střelců (live záznam se
    // stejným startovním číslem pod klíčem, který se teď nezapisuje) a
    // záznam požadovaného střelce, který výsledek ztratil (dropped).
    oldEntries.docs.forEach((d) => {
      const data = d.data();
      if (
        data.source === LIVE_SOURCE &&
        typeof data.startNumber === "number" &&
        (toWrite.has(data.startNumber) || dropped.has(data.startNumber)) &&
        !keepKeys.has(d.id)
      ) {
        staleIds.add(d.id);
      }
    });
  }
  oldEntries.docs.forEach((d) => {
    if (staleIds.has(d.id)) staleRefs.push(d.ref);
  });
  oldStages.docs.forEach((d) => {
    if (staleIds.has(d.id)) staleRefs.push(d.ref);
  });
  if (partial) {
    // Ostatním zveřejněným (vlastní live záznam pod svým klíčem) dorovnat
    // procenta, když oprava změnila body vítěze.
    allShooters.forEach((sh) => {
      if (toWrite.has(sh.startNumber)) return;
      const key = keyOf(sh.startNumber);
      if (staleIds.has(key)) return;
      const data = stored.get(key);
      if (!isLiveEntryOf(data, sh.startNumber) || typeof data.matchPoints !== "number") return;
      const matchPercent = computeRacePercent({ matchPoints: data.matchPoints }, { topScore });
      if (data.matchPercent !== matchPercent) percentUpdates.push({ key, matchPercent });
    });
  }
  // Počet zveřejněných i po dílčí publikaci (fáze 4a, F1): záznamy po zápisu
  const entriesAfter = new Set<string>();
  oldEntries.docs.forEach((d) => {
    if (!staleIds.has(d.id)) entriesAfter.add(d.id);
  });
  writtenKeys.forEach((key) => entriesAfter.add(key));
  const publishedCount = partial ? entriesAfter.size : entryDocs.length;
  // Počet po dílčí publikaci jen u závodů s pravidly 4a (dřív se nepsal)
  const writeCount = !partial || f1;

  // 8) Zápis – dávky postupně v tomto pořadí; metadata a bump jako poslední
  const writer = createBatchWriter(db);
  for (const d of entryDocs) {
    await writer.set(doc(entriesRef, d.key), d.entry);
    await writer.set(doc(stagesRef, d.key), d.stages);
  }
  for (const ref of staleRefs) {
    await writer.delete(ref);
  }
  for (const u of percentUpdates) {
    await writer.update(doc(entriesRef, u.key), { matchPercent: u.matchPercent });
  }
  if (!partial) {
    await writer.set(doc(db, `races/${raceId}/results_metadata/info`), {
      uploadedAt: serverTimestamp(),
      totalEntries: entryDocs.length,
      source: LIVE_SOURCE,
    });
  }
  // Invalidace cache u střelců (resultsPublishedAt na race dokumentu) – úplně poslední
  await writer.update(doc(db, "races", raceId), {
    resultsPublishedAt: serverTimestamp(),
    ...(writeCount ? { resultsPublishedCount: publishedCount } : {}),
  });
  await writer.commit();

  // Lokální cache veřejných výsledků v tomto prohlížeči
  try {
    localStorage.removeItem(`lks_results_${raceId}`);
    localStorage.removeItem("lks_races_list");
  } catch {
    // localStorage nedostupné – ostatní zařízení invaliduje resultsPublishedAt
  }

  return {
    written: entryDocs.length,
    deleted: staleRefs.length,
    missingStartList: withoutStartList.length,
    keptFromEarlier,
    withoutCategory,
  };
}

// ── Porovnání se zveřejněnými výsledky (fáze 4a, D1) ────────────────────────

export interface PublishedDiffInput {
  /** Rundy (aktuální – posluchač). */
  rounds: ReadonlyArray<LiveRound>;
  liveResults: { [startNum: number]: LiveResult };
  /** Nastavená stanoviště (prázdné = nelze porovnat); typ a nastavení pro počet zásahů (4b). */
  stations: ReadonlyArray<HitStationLike>;
  startList: ReadonlyMap<string, StartListRow>;
  /** Dokumenty results_entries (id = klíč). */
  published: ReadonlyArray<{ id: string; data: DocumentData }>;
  /** Pravidla zveřejnění 4a (liveF1Applies); false = závod ukončený před 4a – zveřejní se všichni jako dřív. */
  f1?: boolean;
  /** Pravidla shody 4b (liveTieRulesApply): porovná i počty zásahů (hitCounts). */
  tieRules?: boolean;
}

export interface PublishedDiff {
  /** Startovní čísla, jejichž zveřejněný záznam by nová publikace změnila (chybí, jiné body, rozstřel, jméno, kategorie). */
  changed: number[];
  /** Klíče zveřejněných záznamů živého bodování, které by nová plná publikace smazala (nenastoupivší, mimo rundy, starý klíč). */
  extra: string[];
  /** Kolik střelců se liší (changed + extra). */
  count: number;
  /** Počet zveřejněných záznamů živého bodování. */
  publishedLive: number;
}

/**
 * Liší se zveřejněné výsledky od toho, co by teď zapsala publikace? Porovnává
 * se OBSAH (ne časy – oprava během publikace, smazání, změna kategorie by
 * časy nepoznaly): klíč, body, rozstřel, jméno a kategorie – kategorie jen
 * tam, kde ji publikace určí bez přihlášek a profilů (řádek listiny
 * s údaji z profilu a s rozhodnutím o nováčkovi, host bez účtu) a jen
 * u záznamů s polem categoryId (starší publikace ho nemají). Stejná
 * pravidla jako publikace (klíče nad všemi střelci, zveřejní se jen střelci
 * rund s výsledkem – F1). Záznamy importu se nepočítají.
 */
export function diffPublishedEntries(input: PublishedDiffInput): PublishedDiff {
  const { rounds, liveResults, startList } = input;
  const stationIds = input.stations.map((st) => st.id);
  const { byStart, fromRounds } = liveKeyShooters(rounds, liveResults);
  const keys = assignEntryKeys(Array.from(byStart.values()));
  const publishedLive = new Map<string, DocumentData>();
  input.published.forEach((d) => {
    if (d.data && d.data.source === LIVE_SOURCE) publishedLive.set(d.id, d.data);
  });

  const f1 = input.f1 !== false;
  const changed: number[] = [];
  const expectedKeys = new Set<string>();
  byStart.forEach((sh, startNumber) => {
    const source = fromRounds.get(startNumber);
    const result = liveResults[startNumber];
    // 4a: jen střelci rund s výsledkem; závod ukončený před 4a: všichni
    if (f1 && (!source || !hasLiveResult(result, stationIds))) return;
    const key = keys.get(startNumber) ?? normalizeSequenceNumber(startNumber);
    expectedKeys.add(key);
    const shown = source
      ? roundShooterWithStartList(source, startList)
      : { name: sh.name, isNewbie: false, newbieDisabled: false };
    const row = sh.userId ? startList.get(sh.userId) : undefined;
    const pub = publishedLive.get(key);
    const differs = (): boolean => {
      if (!pub || pub.startNumber !== startNumber) return true;
      const points = liveTotal(result, stationIds);
      if (typeof pub.matchPoints !== "number" || Math.abs(pub.matchPoints - points) > 0.0005) return true;
      if (Math.abs(tiebreakerOf(pub) - tiebreakerOf(result)) > 1e-12) return true;
      if (input.tieRules) {
        // Počty zásahů (4b): úplnost i hodnoty jako by je zapsala publikace
        const expected = liveHitCounts(result, input.stations);
        const complete = pub.hitCountsComplete === true;
        if (complete !== !!expected) return true;
        if (expected) {
          const stored = parseHitCounts(pub.hitCounts);
          if (!stored || !sameHitCounts(stored, expected)) return true;
        }
      }
      if (pub.rawName !== entryRawName(row, { ...sh, name: shown.name }, result)) return true;
      if ("categoryId" in pub) {
        const decided = !sh.userId || (hasProfileData(row) && !!row && row.isNewbie !== undefined);
        if (decided) {
          const newbie =
            !!sh.userId &&
            resolveNewbie(row, undefined, { isNewbie: shown.isNewbie, newbieDisabled: shown.newbieDisabled });
          const expected = sh.userId ? startListEntryCategory(row, newbie).categoryId : null;
          if ((pub.categoryId ?? null) !== expected) return true;
        }
      }
      return false;
    };
    if (differs()) changed.push(startNumber);
  });
  const extra: string[] = [];
  publishedLive.forEach((_data, key) => {
    if (!expectedKeys.has(key)) extra.push(key);
  });
  changed.sort((a, b) => a - b);
  extra.sort();
  return { changed, extra, count: changed.length + extra.length, publishedLive: publishedLive.size };
}
